<template>
  <lb-drawer
    :value="open"
    :heading="drawerHeading"
    width="1010"
    :loading="loading"
    data-testid="setup-tasklist__add-template-dialog"
    @input="onInput"
  >
    <template v-slot:body>
      <div class="add-template-dialog" data-testid="setup-tasklist__template-wizard">
        <div class="add-template-dialog__stepper" data-testid="setup-tasklist__template-stepper">
          <div
            class="add-template-dialog__steps"
            :class="{ 'justify-space-between': isAR }"
          >
            <template v-for="(step, i) in visibleTabs">
              <button
                :key="step.id"
                type="button"
                class="add-template-dialog__step"
                :class="{
                  'add-template-dialog__step--active': activeTab === step.id,
                  'add-template-dialog__step--done': isStepDone(step.id),
                  
                }"
                :data-testid="`setup-tasklist__template-tab--${step.id}`"
                @click="onStepClick(step.id)"
              >
                <span
                  class="add-template-dialog__step-circle"
                  :class="{
                    'add-template-dialog__step-circle--active': activeTab === step.id,
                    'add-template-dialog__step-circle--done': isStepDone(step.id),
                  }"
                >
                  <v-icon v-if="isStepDone(step.id)" small dark>mdi-check</v-icon>
                  <span v-else>{{ i + 1 }}</span>
                </span>
                <span class="add-template-dialog__step-label">{{ step.label }}</span>
              </button>
              <div
                v-if="i < visibleTabs.length - 1"
                :key="`conn-${step.id}`"
                class="add-template-dialog__connector"
                :class="{ 'add-template-dialog__connector--done': isStepDone(step.id) }"
              />
            </template>
          </div>
        </div>

        <div class="add-template-dialog__main">
          <div class="add-template-dialog__body">
            <div
              v-show="activeTab === 'details'"
              class="add-template-dialog__details-step"
              data-testid="setup-tasklist__details-step"
            >
              <DetailsTab
                :key="detailsTabKey"
                :form="form"
                :errors="displayErrors"
                :form-meta="formMeta"
                :chart-of-accounts="chartOfAccounts"
                :custom-fields="customFields"
                @update:form="onFormUpdate"
                @evidence-error="onEvidenceError"
              />

              <TemplateDependencySection
                v-model="dependencyChips"
                :expanded="dependenciesExpanded"
                :subject-duedays="form.duedays"
                :exclude-task-id="excludeTemplateId"
                @update:expanded="onDependenciesExpanded"
              />
            </div>

            <AccountsTab
              v-if="isAR"
              :key="accountsTabKey"
              v-show="activeTab === 'accounts'"
              :gl-codes="form.glcode || []"
              :chart-of-accounts="chartOfAccounts"
              :locations="assignmentLocations"
              :errors="displayErrors"
              @update:glCodes="onAccountsGlCodesUpdate"
            />

            <AssignmentTab
              :key="assignmentTabKey"
              v-show="activeTab === 'assignment'"
              :active="activeTab === ADD_TEMPLATE_TABS.ASSIGNMENT"
              :assignments="assignments"
              :locations="assignmentLocations"
              :users="activeUsersList"
              :all-users="allKnownUsers"
              :is-edit="isEditMode"
              :chart-of-accounts="chartOfAccounts"
              :available-gl-codes="form.glcode || []"
              :task-type="form.task_type"
              :show-threshold="isAR"
              :show-assign-accounts="isAR"
              :errors="displayErrors"
              @update:assignments="onAssignmentsUpdate"
            />
          </div>
        </div>
      </div>
    </template>

    <template v-slot:actions>
      <v-spacer />
      <v-btn
        v-if="!isFirstTab"
        outlined
        class="add-template-dialog__btn text-none secondaryBtn"
        data-testid="setup-tasklist__template-previous"
        @click="goPrevious"
      >
        Previous
      </v-btn>
      <v-btn
        v-if="!isLastTab"
        color="primary"
        class="add-template-dialog__btn text-none white--text"
        data-testid="setup-tasklist__template-next"
        @click="goNext"
      >
        Next
      </v-btn>
      <v-btn
        v-else
        color="primary"
        class="add-template-dialog__btn text-none white--text"
        :loading="loading"
        data-testid="setup-tasklist__template-save"
        @click="onSave"
      >
        Save
      </v-btn>
    </template>
  </lb-drawer>
</template>

<script>
import './AddTemplateDialog.scss';
import DetailsTab from './DetailsTab.vue';
import AccountsTab from './AccountsTab.vue';
import AssignmentTab from './AssignmentTab.vue';
import TemplateDependencySection from './TemplateDependencySection.vue';
import {
  ADD_TEMPLATE_TABS,
  TEMPLATE_DETAIL_FORM_KEYS,
  TEMPLATE_ACCOUNTS_FORM_KEYS,
  TEMPLATE_FIELD_TEST_ID_PREFIX,
  TEMPLATE_ERROR_SCROLL_BLOCK,
} from '../../utils/taskListConstants';
import { DUE_FIELD_KEYS } from '@/modules/financialclose/utils/constants';
import { isAccountReconciliation, validateTemplateForm } from '../../utils/taskListHelpers';
import { normalizeUserIdList } from '../../utils/assignmentRoles';
import { accessErrorsToFieldErrors } from '../../utils/accessErrorsToFieldErrors';
import {
  buildAssignmentUserOptions,
  filterActiveAssignmentCatalog,
  filterAssignmentIdsToKnownUsers,
  formHasInactiveAssignmentUsers,
  inactiveAssignmentIdSet,
} from '../../../../Project/TaskList/drawers/AddTaskDialog/addTaskDialogUtils';
import { resolveEnforceThreshold } from '@/modules/financialclose/utils/enforceThresholdHelpers';
import {
  assignmentsFromTemplate,
  buildTemplatePayload,
  collectTemplateGlcodes,
} from '../../utils/taskListPayload';
import {
  chipsFromDetailArrays,
  chipsHaveMissingLinkType,
  linksPayloadFromChips,
  templateIdString,
} from '../../utils/setupDependencyUtils';
import { pruneAssignmentGlcodesToPool } from '../../utils/glLocationValidation';
import { coerceEvidenceRequired, EVIDENCE_REQUIRED, SETUP_TEMPLATE_DEPENDENCY } from '../../../../../utils/constants';

const EMPTY_FORM = (cycleType, taskType) => ({
  _id: null,
  description: '',
  task_type: taskType,
  cycle_type: cycleType,
  category: '',
  subcategory: '',
  tags: [],
  assertion: [],
  duedays: '',
  priority: 'Medium',
  purpose: '',
  instruction: '',
  glcode: [],
  noofevidence: EVIDENCE_REQUIRED.COUNT_MIN,
  evidence_required: false,
});

function sameFieldValue(key, previous, next) {
  const left = previous?.[key];
  const right = next?.[key];
  if (DUE_FIELD_KEYS.includes(key)) {
    const leftBlank = left == null || left === '';
    const rightBlank = right == null || right === '';
    if (leftBlank || rightBlank) return String(left ?? '') === String(right ?? '');
    const leftNum = Number(left);
    const rightNum = Number(right);
    if (Number.isFinite(leftNum) && Number.isFinite(rightNum)) return leftNum === rightNum;
  }
  return String(left ?? '') === String(right ?? '');
}

const EMPTY_ASSIGNMENT = {
  assignment_number: 1,
  location_ids: [],
  preparer: [],
  approver: [],
  requires_sign_off: false,
  sign_off_approvers: [],
  enforceThreshold: true,
  auto_approve: false,
  glcodes: [],
};

function entityId(entity) {
  if (entity == null) return '';
  if (typeof entity !== 'object') return String(entity);
  const id = entity._id ?? entity.id ?? entity.location_id;
  return id != null && id !== '' ? String(id) : '';
}

function mergeById(...lists) {
  const map = new Map();
  lists.flat().forEach((item) => {
    const id = entityId(item);
    if (!id) return;
    const prev = map.get(id) || {};
    const type = String(item?.type || '').trim() || prev.type || '';
    map.set(id, {
      ...prev,
      ...item,
      _id: id,
      name: item?.name || item?.displayname || prev.name || id,
      email: item?.email || item?.contactemail || prev.email || '',
      isActive: item?.isActive !== undefined ? item.isActive : prev.isActive,
      inactive: item?.inactive !== undefined ? item.inactive : prev.inactive,
      ...(type ? { type } : {}),
    });
  });
  return Array.from(map.values());
}

function pickFormFromTemplate(t = {}, cycleType, defaultTaskType) {
  const cycle = (t.cycle_type || t.cycleType || cycleType || 'FBC').toUpperCase();
  const tagsRaw = t.tags || t.frequency || [];
  const tags = Array.isArray(tagsRaw)
    ? [...tagsRaw]
    : String(tagsRaw || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
  const assertionRaw = t.assertion || t.assertions || [];
  const assertion = Array.isArray(assertionRaw)
    ? [...assertionRaw]
    : String(assertionRaw || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
  const duedaysRaw = t.duedays ?? t.due_days;
  let duedays = '';
  if (duedaysRaw !== '' && duedaysRaw != null && duedaysRaw !== '—') {
    const parsed = Number(duedaysRaw);
    duedays = Number.isFinite(parsed) ? parsed : '';
  }

  return {
    ...EMPTY_FORM(cycle, t.task_type || defaultTaskType),
    _id: t._id || null,
    description: t.description || t.name || '',
    task_type: t.task_type || defaultTaskType,
    cycle_type: cycle,
    category: t.category || t.Category || '',
    subcategory: t.subcategory || t.sub_category || t.SubCategory || '',
    tags,
    assertion,
    duedays,
    priority: t.priority || 'Medium',
    purpose: t.purpose || '',
    instruction: t.instruction || '',
    glcode: collectTemplateGlcodes(t),
    noofevidence: t.no_of_evidence ?? t.noofevidence ?? 0,
    evidence_required: coerceEvidenceRequired(t.evidence_required),
    enforceThreshold: resolveEnforceThreshold(t, t.task_type || defaultTaskType),
  };
}

export default {
  name: 'AddTemplateDialog',

  components: {
    DetailsTab,
    AccountsTab,
    AssignmentTab,
    TemplateDependencySection,
  },

  props: {
    open: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'add',
    },
    taskType: {
      type: String,
      default: null,
    },
    templateId: {
      type: String,
      default: null,
    },
    template: {
      type: Object,
      default: null,
    },
    cycleType: {
      type: String,
      default: 'FBC',
    },
    locations: {
      type: Array,
      default: () => [],
    },
    users: {
      type: Array,
      default: () => [],
    },
    chartOfAccounts: {
      type: Array,
      default: () => [],
    },
    formMeta: {
      type: Object,
      default: () => ({}),
    },
    customFields: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      ADD_TEMPLATE_TABS,
      activeTab: ADD_TEMPLATE_TABS.DETAILS,
      form: EMPTY_FORM(this.cycleType, this.taskType),
      assignments: [],
      dependencyChips: [],
      dependenciesExpanded: false,
      dependenciesExpandLocked: false,
      errors: {},
      /** API field messages client validation does not reproduce (for example due-day order). */
      serverFieldErrors: {},
      /** Ignore input events fired while the drawer is busy or just after a server error is painted. */
      holdServerFieldErrors: false,
      /** Bumped on each dialog open so AccountsTab remounts and clears search. */
      dialogSession: 0,
    };
  },

  computed: {
    isAR() {
      return isAccountReconciliation(this.form.task_type, this);
    },
    isEditMode() {
      return this.mode === 'edit';
    },
    excludeTemplateId() {
      return templateIdString(this.form._id || this.templateId);
    },
    drawerHeading() {
      const typeLabel = this.isAR ? 'Account Reconciliation' : 'Checklist';
      const action = this.mode === 'edit' ? 'Edit' : 'Add';
      return `${action} ${typeLabel} Task Template`;
    },
    /** Merge store locations with GET location_details so selected ids always resolve to names. */
    assignmentLocations() {
      return mergeById(this.locations, this.template?.location_details);
    },
    storeActiveUsersList() {
      return this.$store.getters['tasks/getActiveUsersList'] || [];
    },
    storeInactiveUsersList() {
      return this.$store.getters['tasks/getInactiveUsersList'] || [];
    },
    storeActiveAssignmentIdSet() {
      const set = new Set();
      (this.$store.getters['tasks/getActiveUsersIds'] || []).forEach((id) => {
        if (id) set.add(String(id));
      });
      (this.storeActiveUsersList || []).forEach((user) => {
        const id = entityId(user);
        if (id) set.add(id);
      });
      return set;
    },
    storeInactiveAssignmentIdSet() {
      const set = new Set();
      (this.storeInactiveUsersList || []).forEach((user) => {
        const id = entityId(user);
        if (id) set.add(id);
      });
      return set;
    },
    /**
     * Setup checklist getformdata often returns every tenant user with no isActive
     * flag. Intersect with tasks-store active IDs (v3 getformdata) so the picker
     * matches AddTaskDialog.
     */
    activeUsersList() {
      return filterActiveAssignmentCatalog({
        users: this.users,
        activeIdSet: this.storeActiveAssignmentIdSet,
        inactiveIdSet: this.storeInactiveAssignmentIdSet,
        storeActiveUsers: this.storeActiveUsersList,
      });
    },
    /** Merge store users with GET users_details so preparer/approver chips resolve. */
    allKnownUsers() {
      const fromMap = Object.values(this.template?.users || {});
      const extra = [
        ...this.storeActiveUsersList,
        ...this.storeInactiveUsersList,
        ...Object.values(this.$store.getters['tasks/getUserMap'] || {}),
      ];
      return mergeById(this.users, this.template?.users_details, fromMap, extra);
    },
    activeAssignmentIdSet() {
      const set = new Set(this.storeActiveAssignmentIdSet);
      (this.activeUsersList || []).forEach((user) => {
        const id = entityId(user);
        if (id) set.add(id);
      });
      return set;
    },
    selectedAssignmentIds() {
      return (this.assignments || []).flatMap((a) => [
        ...normalizeUserIdList(a.preparer),
        ...normalizeUserIdList(a.approver),
        ...normalizeUserIdList(a.sign_off_approvers),
      ]);
    },
    assignmentUserOptions() {
      return buildAssignmentUserOptions({
        activeUsers: this.activeUsersList,
        allUsers: this.allKnownUsers,
        selectedIds: this.selectedAssignmentIds,
        isEdit: this.isEditMode,
        activeIdSet: this.activeAssignmentIdSet,
      });
    },
    inactiveUserIds() {
      return inactiveAssignmentIdSet(this.assignmentUserOptions);
    },
    displayedAssignmentIdSet() {
      return new Set(
        (this.assignmentUserOptions || []).map((user) => String(user._id)).filter(Boolean)
      );
    },
    assignmentTabKey() {
      const id = this.templateId || this.template?._id || 'new';
      const loaded = this.template?._id ? 'loaded' : 'pending';
      return `assignment-tab-${this.mode}-${id}-${loaded}`;
    },
    detailsTabKey() {
      const id = this.templateId || this.template?._id || 'new';
      const loaded = this.template?._id ? 'loaded' : 'pending';
      return `details-tab-${this.mode}-${id}-${loaded}`;
    },
    accountsTabKey() {
      const id = this.templateId || this.template?._id || 'new';
      const loaded = this.template?._id ? 'loaded' : 'pending';
      return `accounts-tab-${this.mode}-${id}-${loaded}-${this.dialogSession}`;
    },
    visibleTabs() {
      const tabs = [
        { id: ADD_TEMPLATE_TABS.DETAILS, label: 'Details' },
        { id: ADD_TEMPLATE_TABS.ASSIGNMENT, label: 'Assignment' },
      ];
      if (this.isAR) {
        tabs.splice(1, 0, { id: ADD_TEMPLATE_TABS.ACCOUNTS, label: 'Accounts' });
      }
      return tabs;
    },
    tabOrder() {
      return this.visibleTabs.map((t) => t.id);
    },
    isFirstTab() {
      return this.activeTab === this.tabOrder[0];
    },
    isLastTab() {
      return this.activeTab === this.tabOrder[this.tabOrder.length - 1];
    },
    /** Client errors plus API messages that client rules do not reproduce. */
    displayErrors() {
      return { ...this.errors, ...this.serverFieldErrors };
    },
  },

  watch: {
    async open(val) {
      if (!val) {
        this.dependencyChips = [];
        this.dependenciesExpanded = false;
        this.dependenciesExpandLocked = false;
        return;
      }
      await this.ensureActiveUserCatalog();
      this.hydrate({ resetTab: true });
    },
    // Watch reference only — deep watch was re-hydrating and fighting child updates.
    template(val) {
      if (this.open && val) this.hydrate({ resetTab: false });
    },
  },

  methods: {
    async ensureActiveUserCatalog() {
      if ((this.$store.getters['tasks/getActiveUsersIds'] || []).length) return;
      try {
        await this.$store.dispatch('tasks/fetchFormDataV3');
      } catch (err) {
        console.log('[AddTemplateDialog]:', { fetchFormDataV3: err?.message || err });
      }
      if ((this.$store.getters['tasks/getActiveUsersIds'] || []).length) return;
      try {
        await this.$store.dispatch('tasks/getAllUsersWithStatus', { isActiveUsersRequired: true });
        await this.$store.dispatch('tasks/getAllUsersWithStatus', { isActiveUsersRequired: false });
      } catch (err) {
        console.log('[AddTemplateDialog]:', { getAllUsersWithStatus: err?.message || err });
      }
    },

    hydrate({ resetTab = false } = {}) {
      const defaultTaskType =
        this.taskType || this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.NORMAL;

      if (resetTab) {
        this.dialogSession += 1;
      }

      // Wait for GET /checklist/get/:id before hydrating edit form.
      if (this.mode === 'edit' && this.templateId && !this.template) {
        this.form = EMPTY_FORM(this.cycleType, defaultTaskType);
        this.assignments = [{ ...EMPTY_ASSIGNMENT }];
        this.dependencyChips = [];
        if (resetTab) this.activeTab = ADD_TEMPLATE_TABS.DETAILS;
        this.errors = {};
        this.serverFieldErrors = {};
        this.holdServerFieldErrors = false;
        return;
      }

      if (this.mode === 'edit' && this.template) {
        const t = this.template;
        this.form = pickFormFromTemplate(t, this.cycleType, defaultTaskType);

        (this.customFields || []).forEach((field) => {
          if (!field?.columnName || field.isSystem) return;
          const raw = t[field.columnName];
          const fromCustom = Array.isArray(t.customfields)
            ? t.customfields.find(
                (c) => c && Object.prototype.hasOwnProperty.call(c, field.columnName)
              )
            : null;
          const customVal =
            raw != null
              ? raw
              : fromCustom
                ? fromCustom[field.columnName]
                : field.dataType === 'number'
                  ? 0
                  : '';
          this.$set(this.form, field.columnName, customVal);
        });

        const mapped = assignmentsFromTemplate(t);
        // Replace array reference so AssignmentTab always sees a fresh hydrate.
        this.assignments = mapped.length
          ? mapped.map((a) => ({
              ...a,
              location_ids: [...(a.location_ids || [])].map(String),
              preparer: [...(a.preparer || [])].map(String),
              approver: [...(a.approver || [])].map(String),
              sign_off_approvers: [...(a.sign_off_approvers || [])].map(String),
              requires_sign_off: !!a.requires_sign_off,
              glcodes: [...(a.glcodes || a.glcode || [])].map(String),
              enforceThreshold: resolveEnforceThreshold(a, this.form.task_type),
              auto_approve: !!a.auto_approve,
            }))
          : [{ ...EMPTY_ASSIGNMENT }];
        this.dependencyChips = chipsFromDetailArrays(t);
        this.applyDependenciesExpandedDefault();
      } else {
        this.form = EMPTY_FORM(this.cycleType, defaultTaskType);
        (this.customFields || []).forEach((field) => {
          if (!field?.columnName || field.isSystem) return;
          const defaultVal = field.dataType === 'number' ? 0 : '';
          this.$set(this.form, field.columnName, defaultVal);
        });
        this.assignments = [{ ...EMPTY_ASSIGNMENT }];
        this.dependencyChips = [];
        this.applyDependenciesExpandedDefault();
      }

      if (resetTab) {
        this.activeTab = ADD_TEMPLATE_TABS.DETAILS;
      }
      this.errors = {};
      this.serverFieldErrors = {};
      this.holdServerFieldErrors = false;
    },

    applyDependenciesExpandedDefault() {
      if (this.dependenciesExpandLocked) return;
      this.dependenciesExpanded = this.mode === 'edit' && this.dependencyChips.length > 0;
    },

    onDependenciesExpanded(expanded) {
      this.dependenciesExpandLocked = true;
      this.dependenciesExpanded = expanded;
    },

    onFormUpdate(next) {
      if (!next || typeof next !== 'object') return;
      const previous = this.form || {};
      const hold = this.loading || this.holdServerFieldErrors;
      if (!hold) {
        Object.keys(this.serverFieldErrors).forEach((key) => {
          if (!sameFieldValue(key, previous, next)) {
            this.$delete(this.serverFieldErrors, key);
          }
        });
      }
      this.form = { ...next };
      if (!hold) this.clearResolvedDetailErrors();
    },

    onEvidenceError(error) {
      if (error) {
        const message = Array.isArray(error) ? error[0] : error;
        this.$set(this.errors, 'noofevidence', message);
        return;
      }
      if (this.errors.noofevidence) {
        const next = { ...this.errors };
        delete next.noofevidence;
        this.errors = next;
      }
    },

    onAssignmentsUpdate(next) {
      this.assignments = Array.isArray(next) ? next : [{ ...EMPTY_ASSIGNMENT }];
    },

    onAccountsGlCodesUpdate(glcodes) {
      this.form = { ...this.form, glcode: glcodes };
      this.assignments = pruneAssignmentGlcodesToPool(
        this.assignments,
        glcodes,
        this.chartOfAccounts
      );
    },

    isStepDone(stepId) {
      return this.tabOrder.indexOf(stepId) < this.tabOrder.indexOf(this.activeTab);
    },

    onStepClick(stepId) {
      if (stepId === this.activeTab) return;

      const currentIdx = this.tabOrder.indexOf(this.activeTab);
      const targetIdx = this.tabOrder.indexOf(stepId);
      if (targetIdx < 0) return;

      if (targetIdx < currentIdx) {
        this.activeTab = stepId;
        return;
      }

      for (let i = currentIdx; i < targetIdx; i += 1) {
        const step = this.tabOrder[i];
        if (this.validateWizardStep(step)) continue;

        this.activeTab = step;
        this.$store.commit('sbError', 'Please fill all required fields');
        return;
      }

      this.activeTab = stepId;
    },

    /** Drop detail-step errors that the user has since corrected. */
    clearResolvedDetailErrors() {
      if (!Object.keys(this.errors || {}).length) return;
      // Skip assignments/locations — only detail keys are cleared here.
      const allErrors = validateTemplateForm(this.form, [], this, [], undefined, this.inactiveValidationOptions());
      const next = { ...this.errors };
      let changed = false;
      TEMPLATE_DETAIL_FORM_KEYS.forEach((key) => {
        if (this.serverFieldErrors[key]) return;
        if (next[key] && !allErrors[key]) {
          delete next[key];
          changed = true;
        }
      });
      if (changed) this.errors = next;
    },

    inactiveValidationOptions() {
      return {
        inactiveUserIds: this.inactiveUserIds,
        errorMessages: this.$constants.TASK_LIST_CONSTANTS.ERROR_MESSAGES,
      };
    },

    validateWizardStep(stepId) {
      const keysByStep = {
        [ADD_TEMPLATE_TABS.DETAILS]: TEMPLATE_DETAIL_FORM_KEYS,
        [ADD_TEMPLATE_TABS.ACCOUNTS]: TEMPLATE_ACCOUNTS_FORM_KEYS,
      };
      const keys = keysByStep[stepId];
      if (!keys?.length) return true;

      const allErrors = validateTemplateForm(
        this.form,
        this.assignments,
        this,
        this.assignmentLocations,
        this.$store.state.LocationHeader || 'Location',
        this.inactiveValidationOptions()
      );
      const stepErrors = {};
      keys.forEach((key) => {
        if (allErrors[key]) stepErrors[key] = allErrors[key];
      });

      if (Object.keys(stepErrors).length) {
        this.errors = { ...this.errors, ...stepErrors };
        return false;
      }

      const next = { ...this.errors };
      keys.forEach((key) => {
        if (this.serverFieldErrors[key]) return;
        delete next[key];
      });
      this.errors = next;
      return true;
    },

    validateDetailsStep() {
      return this.validateWizardStep(ADD_TEMPLATE_TABS.DETAILS);
    },

    validateAccountsStep() {
      return this.validateWizardStep(ADD_TEMPLATE_TABS.ACCOUNTS);
    },

    goToFirstErrorTab(errors = {}) {
      if (TEMPLATE_DETAIL_FORM_KEYS.some((key) => errors[key])) {
        this.activeTab = ADD_TEMPLATE_TABS.DETAILS;
        return;
      }
      if (TEMPLATE_ACCOUNTS_FORM_KEYS.some((key) => errors[key])) {
        this.activeTab = ADD_TEMPLATE_TABS.ACCOUNTS;
        return;
      }
      if (Object.keys(errors).some((key) => key.startsWith('assignment_'))) {
        this.activeTab = ADD_TEMPLATE_TABS.ASSIGNMENT;
      }
    },

    /**
     * Paint server fieldErrors from add/edit (access + shape) and jump to Assignment when needed.
     */
    applyServerFieldErrors(fieldErrors = {}) {
      const mapped = accessErrorsToFieldErrors(fieldErrors);
      this.holdServerFieldErrors = true;
      this.serverFieldErrors = { ...mapped };
      this.errors = { ...this.errors, ...mapped };
      this.goToFirstErrorTab(this.displayErrors);
      this.$nextTick(() => {
        this.$nextTick(() => {
          this.holdServerFieldErrors = false;
        });
      });
      this.$nextTick(() => {
        const fieldKey = Object.keys(mapped)[0];
        const target = fieldKey
          ? this.$el.querySelector(`[data-testid="${TEMPLATE_FIELD_TEST_ID_PREFIX}${fieldKey}"]`)
          : null;
        if (target && typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ block: TEMPLATE_ERROR_SCROLL_BLOCK });
        }
      });
      const first = Object.values(mapped).find(Boolean);
      if (first) this.$store.commit('sbError', first);
    },

    goNext() {
      if (!this.validateWizardStep(this.activeTab)) {
        this.$store.commit('sbError', 'Please fill all required fields');
        return;
      }

      const idx = this.tabOrder.indexOf(this.activeTab);
      if (idx < this.tabOrder.length - 1) {
        this.activeTab = this.tabOrder[idx + 1];
      }
    },

    goPrevious() {
      const idx = this.tabOrder.indexOf(this.activeTab);
      if (idx > 0) {
        this.activeTab = this.tabOrder[idx - 1];
      }
    },

    onInput(val) {
      if (!val) this.onCancel();
    },

    sanitizeDisplayedAssignmentIds() {
      const displayedIds = this.displayedAssignmentIdSet;
      this.assignments = (this.assignments || []).map((a) => ({
        ...a,
        preparer: filterAssignmentIdsToKnownUsers(a.preparer, displayedIds),
        approver: filterAssignmentIdsToKnownUsers(a.approver, displayedIds),
        sign_off_approvers: a.requires_sign_off
          ? filterAssignmentIdsToKnownUsers(a.sign_off_approvers, displayedIds)
          : [],
      }));
    },

    assignmentValidationSnackbar() {
      const hasInactiveAssignment = (this.assignments || []).some((a) =>
        formHasInactiveAssignmentUsers(a, this.inactiveUserIds)
      );
      if (hasInactiveAssignment) {
        return this.$constants.TASK_LIST_CONSTANTS.ERROR_MESSAGES?.INACTIVE_ASSIGNMENT_USERS
          || 'Please remove inactive users before saving.';
      }
      return 'Please fill all required fields';
    },

    onCancel() {
      this.errors = {};
      this.serverFieldErrors = {};
      this.holdServerFieldErrors = false;
      this.$emit('cancel');
    },

    onSave() {
      this.sanitizeDisplayedAssignmentIds();
      this.errors = validateTemplateForm(
        this.form,
        this.assignments,
        this,
        this.assignmentLocations,
        this.$store.state.LocationHeader || 'Location',
        this.inactiveValidationOptions()
      );
      const firstError =
        this.errors.duplicate_location || Object.values(this.errors).find(Boolean);
      if (firstError) {
        if (this.inactiveUserIds?.size) {
          console.log('[AddTemplateDialog]:', {
            inactiveUserIds: Array.from(this.inactiveUserIds),
          });
        }
        this.goToFirstErrorTab(this.errors);
        const hasInactiveAssignment = (this.assignments || []).some((a) =>
          formHasInactiveAssignmentUsers(a, this.inactiveUserIds)
        );
        this.$store.commit(
          'sbError',
          hasInactiveAssignment ? this.assignmentValidationSnackbar() : firstError
        );
        return;
      }

      if (this.dependencyChips.length && chipsHaveMissingLinkType(this.dependencyChips)) {
        this.activeTab = ADD_TEMPLATE_TABS.DETAILS;
        this.$store.commit(
          'sbError',
          SETUP_TEMPLATE_DEPENDENCY.SAVE_LINK_TYPE_REQUIRED,
          { root: true }
        );
        return;
      }

      const payload = buildTemplatePayload(
        this.form,
        this.assignments,
        this.customFields,
        this.chartOfAccounts,
        this.assignmentLocations
      );
      payload.links = linksPayloadFromChips(this.dependencyChips);
      this.$emit('save', {
        id: this.form._id || this.templateId,
        data: payload,
        mode: this.mode,
      });
    },
  },
};
</script>
